import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Trash2 } from "lucide-react";

import { adminApi } from "@/lib/api";
import { AdminInput, AdminSelect, AdminTextarea, ConfirmDialog, EmptyState, FieldLabel, Modal, StatusBadge } from "@/components/admin/AdminUI";

const EMPTY = { client_name: "", location: "", quote: "", related_project: "", status: "published", featured: false, sort_order: 0 };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [current, setCurrent] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => adminApi.listTestimonials().then(setItems);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setCurrent(EMPTY); setOpenForm(true); };
  const openEdit = (t) => { setCurrent(t); setOpenForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...current, sort_order: Number(current.sort_order) || 0 };
      if (current.id) await adminApi.updateTestimonial(current.id, payload);
      else await adminApi.createTestimonial(payload);
      toast.success("Saved");
      setOpenForm(false);
      load();
    } catch (err) {
      toast.error("Save failed");
    }
  };

  const toggle = async (t, field) => {
    try {
      const updated = await adminApi.updateTestimonial(t.id, { ...t, [field]: field === "featured" ? !t.featured : t.status === "published" ? "draft" : "published" });
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch { toast.error("Update failed"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try { await adminApi.deleteTestimonial(confirmDelete.id); toast.success("Deleted"); load(); setConfirmDelete(null); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="body-text">Client stories to feature across the website.</p>
        <button type="button" onClick={openCreate} className="btn-primary"><Plus className="h-4 w-4" /> New Testimonial</button>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No testimonials yet" description="Add a client testimonial to feature on the site." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="admin-card rounded-sm p-5 md:p-6 relative">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="eyebrow-muted">{t.location || "—"}</p>
                  <h3 className="text-lg font-semibold mt-2">{t.client_name}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={t.status} />
                  <button type="button" onClick={() => toggle(t, "featured")} className={`h-8 w-8 grid place-items-center rounded-sm border ${t.featured ? "border-brand-yellow text-brand-yellow" : "border-white/10 text-brand-gray hover:text-brand-yellow"}`}><Star className="h-4 w-4" fill={t.featured ? "currentColor" : "none"} /></button>
                </div>
              </div>
              <p className="body-text mt-4 text-sm leading-relaxed line-clamp-6">“{t.quote}”</p>
              <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => openEdit(t)} className="btn-outline text-[10px] px-3 py-2">Edit</button>
                  <button type="button" onClick={() => toggle(t, "status")} className="btn-ghost">Toggle status</button>
                </div>
                <button type="button" onClick={() => setConfirmDelete(t)} className="text-brand-gray hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={openForm} onClose={() => setOpenForm(false)} title={current.id ? "Edit Testimonial" : "New Testimonial"} wide>
        <form onSubmit={save} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <label><FieldLabel>Client name</FieldLabel><AdminInput required value={current.client_name} onChange={(e) => setCurrent({ ...current, client_name: e.target.value })} /></label>
            <label><FieldLabel>Location</FieldLabel><AdminInput value={current.location} onChange={(e) => setCurrent({ ...current, location: e.target.value })} /></label>
          </div>
          <label><FieldLabel>Quote</FieldLabel><AdminTextarea required rows={7} value={current.quote} onChange={(e) => setCurrent({ ...current, quote: e.target.value })} /></label>
          <div className="grid md:grid-cols-3 gap-4">
            <label><FieldLabel>Related project slug</FieldLabel><AdminInput value={current.related_project || ""} onChange={(e) => setCurrent({ ...current, related_project: e.target.value })} placeholder="e.g. casa-m" /></label>
            <label><FieldLabel>Status</FieldLabel>
              <AdminSelect value={current.status} onChange={(e) => setCurrent({ ...current, status: e.target.value })}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </AdminSelect>
            </label>
            <label><FieldLabel>Featured</FieldLabel>
              <AdminSelect value={String(current.featured)} onChange={(e) => setCurrent({ ...current, featured: e.target.value === "true" })}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </AdminSelect>
            </label>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-outline" onClick={() => setOpenForm(false)}>Cancel</button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title="Delete testimonial"
        description={`Delete testimonial from ${confirmDelete?.client_name}?`}
      />
    </div>
  );
}
